import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FocusConfig, LAUNCH_CALLER } from "@spartacus/storefront";
import { combineLatest, Observable, Subscription } from "rxjs";
import { ActiveCartFacade, Cart, OrderEntry, PromotionLocation } from "@spartacus/cart/base/root";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ConverterService, isNotUndefined, Product, PRODUCT_NORMALIZER, RoutingService, User } from "@spartacus/core";
import {
  distinctUntilChanged,
  filter,
  map, pairwise,
  startWith,
  switchMap,
  take,
  tap
} from "rxjs/operators";
import { UserAccountFacade } from "@spartacus/user/account/root";
import { CheckoutStepService } from "@spartacus/checkout/base/components";
import { isEqual } from "../../../shared/helpers/is-equal";
import { findChangedInnerObject } from "../../../shared/helpers/find-changed-inner-object";
import { GenActiveCartService } from "../core/facade/active-cart.service";
import { GenProductService } from 'src/app/generac/services/product.service';
import { GenGaListNames } from 'src/app/generac/enums/ga-list-names.enum';
import { GenLoadingEnum } from 'src/app/generac/enums/loading.enum';
import { GenUserGroup } from 'src/app/generac/enums/user.enum';
import { GenProductTabReference } from 'src/app/generac/models/product.model';
import { GenBaseStoreService } from 'src/app/generac/services/base-store.service';
import { GenGoogleAnalyticsService } from 'src/app/generac/services/google-analytics.service';
import { GenCartUiEventAddToCart } from 'src/app/generac/events/product.event';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { GenLaunchDialogService } from 'src/app/generac/services/launch-dialog.service';

@Component({
  selector: 'gen-added-to-cart-dialog',
  templateUrl: './added-to-cart-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GenAddedToCartDialogComponent implements OnInit, OnDestroy {
  entry$!: Observable<any | undefined>;
  entries!: OrderEntry[];
  isCartLoaded$: Observable<boolean> = this.activeCartService.isStable();
  cart$: Observable<Cart> = this.activeCartService.getActive();
  addedEntryWasMerged$!: Observable<boolean>;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;
  quantity = 0;
  form: UntypedFormGroup = new UntypedFormGroup({});
  focusConfig: FocusConfig = {
    trap: true,
    block: true,
    autofocus: false,
    focusOnEscape: true,
  };
  restrictedCheckoutGroup = true;
  gaListName: string;
  gaEntrySource: string;
  navigatedToPpdFrom: string;
  tooltipMessage: string;
  phoneOrEmail: string;
  itemsWithTabs: GenProductTabReference[];

  @HostListener('click', ['$event'])
  handleClick(event: MouseEvent): void {
    const modalContainer = this.el.nativeElement.querySelector('.cx-modal-content');

    if (!modalContainer?.contains(event.target as Node)) {
      this.dismissModal('Cross click');
    }
  }

  protected readonly GenLoadingEnum = GenLoadingEnum;
  protected subscription = new Subscription();
  protected formSubscription: Subscription;
  private currentEntriesIds: string[] = [];

  constructor(
    private userAccount: UserAccountFacade,
    private baseStoreService: GenBaseStoreService,
    private productService: GenProductService,
    private converter: ConverterService,
    protected activeCartFacade: ActiveCartFacade,
    protected activeCartService: GenActiveCartService,
    protected launchDialogService: GenLaunchDialogService,
    protected routingService: RoutingService,
    protected checkoutStepService: CheckoutStepService,
    protected el: ElementRef,
    protected googleAnalyticsService: GenGoogleAnalyticsService,
    protected checkoutService: GenCheckoutService,
    protected cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.subscribeToDialogData();
    this.subscribeToRoutingState();
    this.getUserAccount();
    this.initGA();
    this.subscribeToBaseInfo();
    this.subscribeToEntries();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
  }

  createQuantityControls() {
    this.subscription.add(
      this.activeCartService.getEntries().pipe(
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        filter((e) => !!e),
      ).subscribe(entries => {
        this.createForm(entries);
        this.subscribeToForm();
      })
    )
  }

  init(
    productCode: string,
    quantity: number,
    numberOfEntriesBeforeAdd: number,
    gaListName: string,
    gaEntrySource: string,
  ): void {
    this.currentEntriesIds.push(productCode);
    this.entry$ = this.activeCartService.getLastEntry(productCode) as Observable<any>;
    this.createQuantityControls();
    this.quantity = quantity;
    this.addedEntryWasMerged$ = this.getAddedEntryWasMerged(
      numberOfEntriesBeforeAdd,
      productCode
    );
    this.gaListName = gaListName;
    this.gaEntrySource = gaEntrySource;
  }

  dismissModal(reason?: any): void {
    this.subscription.add(
      this.activeCartFacade.getActive().subscribe(cart => {
        this.checkoutStepService.resetSteps();
        this.checkoutService.disableCheckoutSteps(cart.cartTypes, cart.engineReplacement);
        this.launchDialogService.closeDialog(reason, LAUNCH_CALLER.ADDED_TO_CART);
      })
    )
  }

  onAccessoryAdd(accessor: { productCode: string, quantity: string }) {
    this.activeCartService.addEntry(accessor.productCode, +accessor.quantity, this.gaListName);

    if (!this.currentEntriesIds.includes(accessor.productCode)) {
      this.currentEntriesIds.push(accessor.productCode);
    }
  }

  getControl(entry: OrderEntry): UntypedFormGroup {
    return <UntypedFormGroup>this.form.get(this.getControlName(entry)).get('quantity');
  }

  protected getAddedEntryWasMerged(
    numberOfEntriesBeforeAdd: number,
    productCode: string
  ): Observable<boolean> {
    return this.isCartLoaded$.pipe(
      filter((loaded) => loaded),
      switchMap(() => this.activeCartFacade.getEntries()),
      map((entries) => {
        const entriesCount = entries.filter(entry => this.currentEntriesIds.includes(entry.product.code))?.length;

        if (entries.length === 0 && numberOfEntriesBeforeAdd === 0) {
          this.dismissModal('Cross click');
        }
        if (entriesCount === 0) {
          this.dismissModal('Cross click');
        }
        return entries.length === numberOfEntriesBeforeAdd;
      })
    );
  }

  private subscribeToDialogData() {
    this.subscription.add(
      this.launchDialogService.data$
        .pipe(
          tap((dialogData: GenCartUiEventAddToCart) => this.init(
            dialogData.productCode,
            dialogData.quantity,
            dialogData.numberOfEntriesBeforeAdd,
            dialogData.gaListName,
            dialogData.gaEntrySource
          )),
          switchMap(dialogData => this.getProductTabsReferences(dialogData.productCode)),
          tap((productTabReferences: GenProductTabReference[]) => this.itemsWithTabs = productTabReferences)
        ).subscribe()
    )
  }

  private subscribeToRoutingState() {
    this.subscription.add(
      this.routingService
        .getRouterState()
        .pipe(
          tap(state => {
            if (this.gaListName !== GenGaListNames.WARRANTIES) {
              this.navigatedToPpdFrom = state.state?.queryParams?.gaListName;
            }
          }),
          filter((state) => !!state.nextState)
        ).subscribe(() => this.dismissModal('dismiss'))
    );
  }

  private subscribeToEntries(): void {
    this.subscription.add(
      this.getEntries().subscribe(entries => {
        this.entries = entries;
        this.cdr.markForCheck();
      })
    );

  }

  private getUserAccount(): void {
    this.subscription.add(
      this.userAccount.get()
        .pipe(
          take(1),
          tap((user: User) => {
            if (user) {
              this.restrictedCheckoutGroup = user.roles?.includes(GenUserGroup.restrictedCheckoutGroup);
            }
          }),
        ).subscribe()
    )
  }

  private initGA() {
    if (this.entry$) {
      this.entry$.pipe(
        filter(isNotUndefined),
        take(1)
      ).subscribe((entry) => {
        const product = {
          ...entry.product,
          price: {
            value: entry.basePrice.value
          }
        };
        const quantity = this.quantity < entry?.product?.addToCartQtyMultiplier ? +(entry?.product?.addToCartQtyMultiplier) : this.quantity;
        this.googleAnalyticsService.sendGaEvent('add_to_cart', {
          currency: entry.totalPrice.currencyIso,
          value: entry.basePrice.value * quantity,
          items: [this.googleAnalyticsService.mapProductToGaItem(product, 0, quantity, this.gaListName, this.navigatedToPpdFrom, this.gaEntrySource)],
        });
      })
    }
  }

  private getProductTabsReferences(productCode: string): Observable<GenProductTabReference[]> {
    this.itemsWithTabs = null;
    return this.productService.getProductTabReferences(productCode).pipe(
      take(1),
      map((productTabReferences: GenProductTabReference[]) => {
        return productTabReferences.map(productTabReference => {
          return {
            tab: productTabReference.tab,
            products: productTabReference.products.map((product: Product) => {
              return this.converter.convert({
                ...product,
                productCode: product.code,
              }, PRODUCT_NORMALIZER);
            }) as Product[],
          }
        })
      })
    );
  }

  private subscribeToBaseInfo() {
    this.subscription.add(
      combineLatest([
        this.baseStoreService.getTooltipMessage(),
        this.baseStoreService.getPhoneNumber(),
      ]).subscribe(([message, phoneOrEmail]) => {
        this.tooltipMessage = message?.message;
        this.phoneOrEmail = phoneOrEmail?.contactInfo;
      })
    );
  }

  private getControlName(item: OrderEntry): string {
    return item.entryNumber?.toString() || '';
  }

  private createForm(entries: OrderEntry[]): void {
    entries.forEach((item) => {
      const controlName = this.getControlName(item);
      const control = this.form.get(controlName);
      if (control) {
        if (control.get('quantity')?.value !== item.quantity) {
          control.patchValue({ quantity: item.quantity }, { emitEvent: false });
        }
      } else {
        const group = new UntypedFormGroup({
          entryNumber: new UntypedFormControl(item.entryNumber),
          quantity: new UntypedFormControl(item.quantity, { updateOn: 'blur' }),
        });
        this.form.addControl(controlName, group);
      }

      if (!item.updateable) {
        this.form.controls[controlName].disable();
      }
    });
  }

  private getEntries() {
    return this.activeCartService.getEntries()
      .pipe(
        map(entries => entries.filter(entry => this.currentEntriesIds.includes(entry.product.code))),
        filter(entries => !!entries?.length),
        tap(entries => {
          this.createForm(entries);
          if (this.currentEntriesIds.length > entries.length) {
            this.currentEntriesIds = this.currentEntriesIds.filter(currentId => entries.find(entry => currentId === entry.product.code))
          }
        }),
        map(entries => this.currentEntriesIds.map(currentId => entries.find(entry => entry.product.code === currentId)))
      )
  }

  private subscribeToForm() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    this.formSubscription = this.form.valueChanges
      .pipe(
        startWith(this.form.value),
        filter(value => !!value),
        distinctUntilChanged(isEqual),
        pairwise(),
        map(([previous, current]) => findChangedInnerObject(previous, current)?.changedValue),
        tap(value => {
          const entry = this.entries?.find(entry => value?.entryNumber === entry.entryNumber);

          if (entry && value) {
            this.activeCartService.updateEntry(
              value.entryNumber,
              value.quantity
            );
          }
        })
      ).subscribe();
  }
}
